import { api } from "./index";
import {
    AutoReferralAssignment,
    AutoReferralAssignmentsFilters,
    AutoReferralPayment,
    AutoReferralPaymentsFilters,
    AutoReferralProgram,
    AutoReferralProgramPhoto,
    AutoReferralStatsResponse,
    AutoReferralSubscription,
    AutoReferralSubscriptionFilters,
    CreateAutoReferralProgramPayload,
    PaginatedResponse,
    UpdateAutoReferralProgramPayload,
} from "../types/autoReferral";

const buildProgramFormData = (
    payload: Partial<
        CreateAutoReferralProgramPayload | UpdateAutoReferralProgramPayload
    >
): FormData => {
    const formData = new FormData();

    if (payload.translations !== undefined) {
        if (Array.isArray(payload.translations)) {
            payload.translations.forEach((translation, index) => {
                const baseKey = `translations[${index}]`;
                formData.append(`${baseKey}[locale]`, translation.locale);
                formData.append(`${baseKey}[name]`, translation.name);

                if (
                    translation.short_description !== undefined &&
                    translation.short_description !== null
                ) {
                    formData.append(
                        `${baseKey}[short_description]`,
                        translation.short_description,
                    );
                }

                if (
                    translation.brief_description !== undefined &&
                    translation.brief_description !== null
                ) {
                    formData.append(
                        `${baseKey}[brief_description]`,
                        translation.brief_description,
                    );
                }

                if (
                    translation.description !== undefined &&
                    translation.description !== null
                ) {
                    formData.append(
                        `${baseKey}[description]`,
                        translation.description,
                    );
                }
            });
        } else {
            formData.append("translations", JSON.stringify(payload.translations));
        }
    }
    if (payload.min_subscription_amount !== undefined) {
        formData.append("min_subscription_amount", payload.min_subscription_amount);
    }
    if (payload.max_coefficient !== undefined) {
        formData.append("max_coefficient", String(payload.max_coefficient));
    }
    if (payload.referral_code !== undefined) {
        formData.append("referral_code", payload.referral_code);
    }
    if (payload.sort_order !== undefined) {
        formData.append("sort_order", String(payload.sort_order));
    }
    if (payload.is_subscription_active !== undefined) {
        formData.append(
            "is_subscription_active",
            payload.is_subscription_active ? "1" : "0"
        );
    }
    if (payload.logo instanceof File) {
        formData.append("logo", payload.logo);
    }

    return formData;
};

const adaptMutationHook = (
    hook: () => readonly [(...args: any[]) => any, any],
) => () => {
    const [trigger, state] = hook();
    return {
        ...state,
        mutate: trigger,
        mutateAsync: async (...args: any[]) => {
            const result = trigger(...args);
            return result.unwrap ? result.unwrap() : result;
        },
    };
};

export interface AutoReferralProgramsQueryParams {
    page?: number;
    per_page?: number;
    sort?: string;
    direction?: "asc" | "desc";
}

const autoReferralApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getAutoReferralPrograms: builder.query<
            PaginatedResponse<AutoReferralProgram>,
            AutoReferralProgramsQueryParams | undefined
        >({
            query: (params: AutoReferralProgramsQueryParams = {}) => ({
                url: "/admin/auto-referral-programs",
                params: params ?? undefined,
            }),
            providesTags: (result) => [
                { type: "AutoReferralPrograms", id: "LIST" },
                ...(result?.data?.map((program) => ({
                    type: "AutoReferralPrograms" as const,
                    id: program.id,
                })) ?? []),
            ],
        }),
        getAutoReferralProgram: builder.query<
            { data: AutoReferralProgram },
            number
        >({
            query: (programId) => `/admin/auto-referral-programs/${programId}`,
            providesTags: (_result, _error, programId) => [
                { type: "AutoReferralPrograms", id: programId },
            ],
        }),
        getAutoReferralProgramStats: builder.query<
            AutoReferralStatsResponse,
            number
        >({
            query: (programId) =>
                `/admin/auto-referral-programs/${programId}/stats`,
            providesTags: (_result, _error, programId) => [
                { type: "AutoReferralStats", id: programId },
            ],
        }),
        createAutoReferralProgram: builder.mutation<
            any,
            CreateAutoReferralProgramPayload
        >({
            query: (payload) => ({
                url: "/admin/auto-referral-programs",
                method: "POST",
                body: buildProgramFormData(payload),
            }),
            invalidatesTags: [{ type: "AutoReferralPrograms", id: "LIST" }],
        }),
        updateAutoReferralProgram: builder.mutation<
            any,
            UpdateAutoReferralProgramPayload
        >({
            query: ({ id, ...rest }) => {
                const formData = buildProgramFormData(rest);
                formData.append("_method", "PATCH");
                return {
                    url: `/admin/auto-referral-programs/${id}`,
                    method: "POST",
                    body: formData,
                };
            },
            invalidatesTags: (_result, _error, { id }) => [
                { type: "AutoReferralPrograms", id },
                { type: "AutoReferralPrograms", id: "LIST" },
                { type: "AutoReferralStats", id },
            ],
        }),
        deleteAutoReferralProgram: builder.mutation<any, number>({
            query: (programId) => ({
                url: `/admin/auto-referral-programs/${programId}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, programId) => [
                { type: "AutoReferralPrograms", id: programId },
                { type: "AutoReferralPrograms", id: "LIST" },
            ],
        }),
        getAutoReferralGallery: builder.query<
            { data: AutoReferralProgramPhoto[] },
            number
        >({
            query: (programId) =>
                `/admin/auto-referral-programs/${programId}/gallery`,
            providesTags: (_result, _error, programId) => [
                { type: "AutoReferralGallery", id: programId },
            ],
        }),
        uploadAutoReferralPhoto: builder.mutation<
            any,
            {
                programId: number;
                file: File;
                isActive?: boolean;
                sortOrder?: number;
            }
        >({
            query: ({ programId, file, isActive, sortOrder }) => {
                const formData = new FormData();
                formData.append("photo", file);
                if (isActive !== undefined) {
                    formData.append("is_active", isActive ? "1" : "0");
                }
                if (sortOrder !== undefined) {
                    formData.append("sort_order", String(sortOrder));
                }
                return {
                    url: `/admin/auto-referral-programs/${programId}/gallery`,
                    method: "POST",
                    body: formData,
                };
            },
            invalidatesTags: (_result, _error, { programId }) => [
                { type: "AutoReferralGallery", id: programId },
            ],
        }),
        updateAutoReferralPhoto: builder.mutation<
            any,
            {
                programId: number;
                photoId: number;
                file?: File;
                isActive?: boolean;
                sortOrder?: number;
            }
        >({
            query: ({ programId, photoId, file, isActive, sortOrder }) => {
                const formData = new FormData();
                formData.append("_method", "PATCH");
                if (file) {
                    formData.append("photo", file);
                }
                if (isActive !== undefined) {
                    formData.append("is_active", isActive ? "1" : "0");
                }
                if (sortOrder !== undefined) {
                    formData.append("sort_order", String(sortOrder));
                }
                return {
                    url: `/admin/auto-referral-programs/${programId}/gallery/${photoId}`,
                    method: "POST",
                    body: formData,
                };
            },
            invalidatesTags: (_result, _error, { programId }) => [
                { type: "AutoReferralGallery", id: programId },
            ],
        }),
        deleteAutoReferralPhoto: builder.mutation<
            any,
            { programId: number; photoId: number }
        >({
            query: ({ programId, photoId }) => ({
                url: `/admin/auto-referral-programs/${programId}/gallery/${photoId}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, { programId }) => [
                { type: "AutoReferralGallery", id: programId },
            ],
        }),
        getAutoReferralSubscriptions: builder.query<
            PaginatedResponse<AutoReferralSubscription>,
            { programId: number; filters?: AutoReferralSubscriptionFilters }
        >({
            query: ({ programId, filters = {} }) => ({
                url: `/admin/auto-referral-programs/${programId}/subscriptions`,
                params: filters,
            }),
            providesTags: (_result, _error, { programId }) => [
                { type: "AutoReferralSubscriptions", id: programId },
            ],
        }),
        updateAutoReferralSubscription: builder.mutation<
            any,
            {
                programId: number;
                subscriptionId: number;
                payload: Partial<{
                    coefficient: number;
                    billing_day: number;
                    is_active: boolean;
                }>;
            }
        >({
            query: ({ programId, subscriptionId, payload }) => ({
                url: `/admin/auto-referral-programs/${programId}/subscriptions/${subscriptionId}`,
                method: "PATCH",
                body: payload,
            }),
            invalidatesTags: (_result, _error, { programId }) => [
                { type: "AutoReferralSubscriptions", id: programId },
            ],
        }),
        getAutoReferralPayments: builder.query<
            PaginatedResponse<AutoReferralPayment> & {
                summary?: { total_amount: string };
            },
            { programId: number; filters?: AutoReferralPaymentsFilters }
        >({
            query: ({ programId, filters = {} }) => ({
                url: `/admin/auto-referral-programs/${programId}/payments`,
                params: filters,
            }),
            providesTags: (_result, _error, { programId }) => [
                { type: "AutoReferralPayments", id: programId },
            ],
        }),
        createAutoReferralPayment: builder.mutation<
            any,
            {
                programId: number;
                subscriptionId: number;
                amount: string;
                currency: "USD" | "USDT";
                coefficient?: number;
                charged_at?: string;
            }
        >({
            query: ({ programId, subscriptionId, ...body }) => ({
                url: `/admin/auto-referral-programs/${programId}/subscriptions/${subscriptionId}/payments`,
                method: "POST",
                body,
            }),
            invalidatesTags: (_result, _error, { programId }) => [
                { type: "AutoReferralPayments", id: programId },
                { type: "AutoReferralSubscriptions", id: programId },
                { type: "AutoReferralStats", id: programId },
            ],
        }),
        getAutoReferralAssignments: builder.query<
            PaginatedResponse<AutoReferralAssignment>,
            { programId: number; filters?: AutoReferralAssignmentsFilters }
        >({
            query: ({ programId, filters = {} }) => ({
                url: `/admin/auto-referral-programs/${programId}/assignments`,
                params: filters,
            }),
            providesTags: (_result, _error, { programId }) => [
                { type: "AutoReferralAssignments", id: programId },
            ],
        }),
    }),
    overrideExisting: false,
});

const {
    useGetAutoReferralProgramsQuery,
    useGetAutoReferralProgramQuery,
    useGetAutoReferralProgramStatsQuery,
    useCreateAutoReferralProgramMutation,
    useUpdateAutoReferralProgramMutation,
    useDeleteAutoReferralProgramMutation,
    useGetAutoReferralGalleryQuery,
    useUploadAutoReferralPhotoMutation,
    useUpdateAutoReferralPhotoMutation,
    useDeleteAutoReferralPhotoMutation,
    useGetAutoReferralSubscriptionsQuery,
    useUpdateAutoReferralSubscriptionMutation,
    useGetAutoReferralPaymentsQuery,
    useCreateAutoReferralPaymentMutation,
    useGetAutoReferralAssignmentsQuery,
} = autoReferralApi;

export const useAutoReferralPrograms = (
    params: AutoReferralProgramsQueryParams = {}
) => useGetAutoReferralProgramsQuery(params);

export const useAutoReferralProgram = (programId: number | null) =>
    useGetAutoReferralProgramQuery(programId as number, {
        skip: programId === null,
    });

export const useAutoReferralProgramStats = (programId: number | null) =>
    useGetAutoReferralProgramStatsQuery(programId as number, {
        skip: programId === null,
    });

export const useAutoReferralGallery = (programId: number | null) =>
    useGetAutoReferralGalleryQuery(programId as number, {
        skip: programId === null,
    });

export const useAutoReferralSubscriptions = (
    programId: number | null,
    filters: AutoReferralSubscriptionFilters = {}
) =>
    useGetAutoReferralSubscriptionsQuery(
        { programId: programId as number, filters },
        { skip: programId === null }
    );

export const useAutoReferralPayments = (
    programId: number | null,
    filters: AutoReferralPaymentsFilters = {}
) =>
    useGetAutoReferralPaymentsQuery(
        { programId: programId as number, filters },
        { skip: programId === null }
    );

export const useCreateAutoReferralProgram = adaptMutationHook(
    useCreateAutoReferralProgramMutation
);
export const useUpdateAutoReferralProgram = adaptMutationHook(
    useUpdateAutoReferralProgramMutation
);
export const useDeleteAutoReferralProgram = adaptMutationHook(
    useDeleteAutoReferralProgramMutation
);
export const useUploadAutoReferralPhoto = adaptMutationHook(
    useUploadAutoReferralPhotoMutation
);
export const useUpdateAutoReferralPhoto = adaptMutationHook(
    useUpdateAutoReferralPhotoMutation
);
export const useDeleteAutoReferralPhoto = adaptMutationHook(
    useDeleteAutoReferralPhotoMutation
);
export const useUpdateAutoReferralSubscription = adaptMutationHook(
    useUpdateAutoReferralSubscriptionMutation
);
export const useCreateAutoReferralPayment = adaptMutationHook(
    useCreateAutoReferralPaymentMutation
);

export const useAutoReferralAssignments = (
    programId: number | null,
    filters: AutoReferralAssignmentsFilters = {}
) =>
    useGetAutoReferralAssignmentsQuery(
        { programId: programId as number, filters },
        { skip: programId === null }
    );
